import * as pc from 'playcanvas';
import { pickDiag } from './pickDiagnostics';

/**
 * Is unified GPU splat picking available right now?
 *
 * Engine >=2.21 supports pc.Picker over layers containing UNIFIED gsplats, but only
 * when the active unified renderer sorts on the GPU: GSplatManager.prepareForPicking
 * returns null for CPU-sorted renderers (WebGL2 raster-cpu), so unified splats simply
 * never land in the pick buffer there. Live-probed (not cached) because the unified
 * renderer can fall back at device init and the escape hatch can be toggled between
 * sessions. Escape hatch: localStorage 'storysplat:forceCpuPick' = '1' returns the
 * runtime to CPU-only picking.
 */
export function unifiedGpuSplatPickSupported(app: pc.Application): boolean {
    try {
        if (typeof localStorage !== 'undefined' && localStorage.getItem('storysplat:forceCpuPick') === '1') return false;
        if (!(app.graphicsDevice as pc.GraphicsDevice & { isWebGPU?: boolean }).isWebGPU) return false;
        const PC = pc as unknown as Record<string, unknown>;
        const rasterGpuSort = (PC.GSPLAT_RENDERER_RASTER_GPU_SORT as number | undefined) ?? 2;
        const compute = (PC.GSPLAT_RENDERER_COMPUTE as number | undefined) ?? 3;
        const current = (app.scene.gsplat as unknown as { currentRenderer?: number } | undefined)?.currentRenderer;
        return current === rasterGpuSort || current === compute;
    } catch {
        return false;
    }
}

/**
 * SplatDepthPicker — GPU depth pick of the UNIFIED gsplat (engine >= 2.21).
 *
 * WHY THIS SHAPE
 * On PlayCanvas 2.20, a pc.Picker pass over a layer containing the unified gsplat
 * re-dispatched the shared work-buffer compute (shimmer) and, on Windows/NVIDIA
 * (Dawn/D3D12), intermittently hung the device (DXGI_ERROR_DEVICE_HUNG). This class
 * used to dodge that by switching the splat to a NON-unified component on an isolated
 * pick layer. Engine 2.21.0 fixed the hang in the unified pick path itself — the
 * hybrid renderer submits pending work between the sort's indirect-arg writes and the
 * dispatches that consume them in pick mode (playcanvas/engine#9036) — so the splat
 * now STAYS UNIFIED and the picker renders the World layer directly. No component
 * switching, no dedicated pick layer, nothing to restore on end(). Octree/LOD splats
 * are included: the unified world picks whatever it renders.
 *
 * WHERE IT WORKS (begin() gates; callers fall back to the CPU raycast)
 * - WebGPU with a GPU-sort unified renderer (raster-gpu / compute) — see
 *   unifiedGpuSplatPickSupported. WebGL2 raster-cpu has no unified pick path.
 * - Not per-frame: this is for point queries (measurement clicks, throttled hover).
 *   The every-frame reticle stays on the synchronous CPU raycast.
 *
 * The pick renders at 0.25 scale into the picker's depth MRT; getWorldPointAsync
 * yields the world point along the pixel ray at the frontmost surface's depth —
 * splat or mesh — and the selection says which of the two it was.
 */
export class SplatDepthPicker {
    private _app: pc.Application;
    private _picker: pc.Picker | null = null;
    private _camera: pc.Entity | null = null;
    private _active = false;

    /** Pick buffer resolution as a fraction of CSS pixels (matches the pre-unified reticle picker). */
    private readonly _pickScale = 0.25;

    constructor(app: pc.Application) {
        this._app = app;
    }

    get active(): boolean {
        return this._active;
    }

    /**
     * Begin a pick session against the unified splat. Returns false (safe no-op) when
     * unified GPU picking isn't available — the caller stays on the CPU pick.
     */
    begin(camera: pc.Entity): boolean {
        if (this._active) { pickDiag.push('begin:already-active'); return true; }
        if (!unifiedGpuSplatPickSupported(this._app)) {
            pickDiag.push('begin:REJECT unified GPU pick unsupported', {
                webgpu: !!(this._app.graphicsDevice as pc.GraphicsDevice & { isWebGPU?: boolean }).isWebGPU,
                renderer: (this._app.scene.gsplat as unknown as { currentRenderer?: number } | undefined)?.currentRenderer ?? null,
            });
            return false;
        }
        this._camera = camera;
        this._picker = new pc.Picker(this._app, 1, 1, true); // depth=true → getWorldPointAsync
        this._active = true;
        pickDiag.push('begin:unified-gpu-pick');
        return true;
    }

    /**
     * Pick the world-space surface point under (sxCss, syCss) in CSS px. Null if nothing
     * is there or the pick fails — the caller should then fall back to the CPU raycast.
     * `mesh` is true when the frontmost thing there is a mesh (a model, a primitive) rather than
     * a splat: splats have no pick id in the selection, meshes do. A splat counts once its alpha
     * at the pixel passes the scene's gsplat alphaClip (0.3), however much shows through it, so
     * measurements prefer the CPU pick's haze-aware crossing for splats (createViewer pickWorldPoint).
     */
    async pickWorldPoint(sxCss: number, syCss: number): Promise<{ point: pc.Vec3; mesh: boolean } | null> {
        if (!this._active || !this._picker || !this._camera) return null;
        const cam = this._camera.camera;
        if (!cam) return null;
        try {
            const canvas = this._app.graphicsDevice.canvas;
            const pw = Math.max(1, Math.floor(canvas.clientWidth * this._pickScale));
            const ph = Math.max(1, Math.floor(canvas.clientHeight * this._pickScale));
            const px = Math.min(pw - 1, Math.max(0, Math.floor(sxCss * this._pickScale)));
            const py = Math.min(ph - 1, Math.max(0, Math.floor(syCss * this._pickScale)));
            this._picker.resize(pw, ph);
            const worldLayer = this._app.scene.layers.getLayerByName('World');
            if (!worldLayer) { pickDiag.push('pick:REJECT no World layer'); return null; }
            // Renders the World layer in pick mode — the unified director re-runs
            // cull/projection/sort for the pick view. The engine submission-splits the
            // sort's indirect dispatches in pick mode (#9036), which is what makes this
            // safe on the Windows/NVIDIA drivers that used to TDR.
            const tPrep = performance.now();
            this._picker.prepare(cam, this._app.scene, [worldLayer]);
            const prepMs = Math.round((performance.now() - tPrep) * 10) / 10;
            // The readback below is the mapAsync — if a TDR is brewing, THIS is
            // where it surfaces. Separate timing so we can tell prepare (render)
            // cost from readback (sync-with-GPU) cost.
            const tRead = performance.now();
            const [wp, selection] = await Promise.all([
                this._picker.getWorldPointAsync(px, py),
                this._picker.getSelectionAsync(px, py).catch(() => [] as pc.MeshInstance[]),
            ]);
            const readMs = Math.round((performance.now() - tRead) * 10) / 10;
            const ok = !!(wp && isFinite(wp.x) && isFinite(wp.y) && isFinite(wp.z));
            const mesh = selection.some((item) => item instanceof pc.MeshInstance);
            pickDiag.push('pick', { px, py, pw, ph, prepMs, readMs, hit: ok, mesh, dist: ok ? Math.round(this._camera.getPosition().distance(wp!) * 100) / 100 : null });
            return ok ? { point: wp!.clone(), mesh } : null;
        } catch (e) {
            pickDiag.push('pick:ERROR', { error: String(e) });
            console.warn('[SplatDepthPicker] pick failed:', e);
            return null;
        }
    }

    /** End the session: destroy the picker. Nothing to restore — the splat never left unified. Idempotent. */
    end(): void {
        if (!this._active) return;
        pickDiag.push('end');
        this._picker?.destroy();
        this._picker = null;
        this._camera = null;
        this._active = false;
    }
}
